import { useCallback, useEffect, useState } from 'react';
import Table, { Order } from '../../../components/Table';
import { IPaginationData } from '../../../api/base';
import moment from 'moment';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import theme from '../../../theme';
import { ITrainingDocument } from '../../../models/training-document.model';
import documentApi from '../../../api/document.api';
import { LogoImage } from '../../styled';
import fileApi from '../../../api/file.api';
import { getFileName } from '../../../utils/common';
import ImageUploadWithPreview from '../../../components/ImageUpload';

export default function TrainingDocumentListPage() {
  const [dataSource, setDataSource] = useState<
    IPaginationData<ITrainingDocument>
  >({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });
  const [loading, setLoading] = useState(false);
  const [[orderBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >(['code', 'desc']);

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
  });

  const [currItem, setCurrItem] = useState<
    Partial<ITrainingDocument> | undefined
  >();
  const [currDeletedId, setCurrDeletedId] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    documentApi
      .listing({
        ...filter,
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .catch((e) => {
        toast.error(e.message ?? e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  }, [order, orderBy, filter]);

  const handleUploadFile = (
    fieldName: 'file_url_en' | 'file_url_vi',
    file: File,
  ) => {
    return fileApi.upload(file).then((res) => {
      setCurrItem((prev) => ({
        ...prev,
        [fieldName]: res.data.path,
      }));
    });
  };

  useEffect(loadData, [loadData]);

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="h3">Training document</Typography>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(4),
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              name="search"
              sx={{
                '& .MuiInputBase-root': {
                  pr: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      type="submit"
                      sx={{ minWidth: 48, borderRadius: 0 }}
                    >
                      <Search />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <div>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => {
                setCurrItem({});
              }}
            >
              Create new
            </Button>
          </div>
        </div>
      </Box>

      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={orderBy}
        loading={loading}
        // onClick={setCurrItem}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'photo_url',
            title: 'Photo',
            dataKey: 'photo_url',
            render: (data) =>
              data.thumbnail ? (
                <LogoImage
                  src={process.env.REACT_APP_API_URI + data.thumbnail}
                />
              ) : null,
          },
          {
            key: 'title',
            title: 'Title',
            dataKey: 'title_en',
            render: (data) => (
              <>
                <div className="one-lines">{data.title_vi}</div>
                <div className="one-lines">{data.title_en}</div>
              </>
            ),
          },
          {
            key: 'file',
            title: 'File',
            dataKey: 'file',
            render: (data) => (
              <>
                {!!data.file_url_vi && (
                  <div>
                    <span>Vietnamese: </span>
                    <a
                      href={`${process.env.REACT_APP_API_URI}${data.file_url_vi}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download
                    </a>
                  </div>
                )}
                {!!data.file_url_en && (
                  <div>
                    <span>English: </span>
                    <a
                      href={`${process.env.REACT_APP_API_URI}${data.file_url_en}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download
                    </a>
                  </div>
                )}
              </>
            ),
          },

          {
            key: 'created_at',
            title: 'Created at',
            dataKey: 'created_at',
            render: (data) =>
              moment(data.created_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'updated_at',
            title: 'Updated at',
            dataKey: 'updated_at',
            render: (data) =>
              moment(data.updated_at).format('DD/MM/YYYY HH:mm'),
          },
          {
            key: 'actions',
            title: 'Actions',
            dataKey: 'id',
            render: (data) => (
              <>
                <IconButton onClick={() => setCurrItem(data)}>
                  <Edit />
                </IconButton>
                <IconButton onClick={() => setCurrDeletedId(data.id)}>
                  <Delete />
                </IconButton>
              </>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />

      <Dialog
        open={!!currDeletedId}
        onClose={() => setCurrDeletedId(undefined)}
      >
        <DialogContent>
          <DialogContentText color={'black'}>
            This action cannot be revert. Do you want to delete this document?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrDeletedId(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => {
              if (currDeletedId) {
                setDeleting(true);
                documentApi
                  .remove(currDeletedId)
                  .then(() => {
                    loadData();
                    toast.success('Delete submission successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrDeletedId(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="error"
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!currItem}
        onClose={() => setCurrDeletedId(undefined)}
        maxWidth="xl"
      >
        <DialogTitle>
          {currItem?.id ? 'Update document' : 'Create new document'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" p={1}>
                Thumbnail
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <ImageUploadWithPreview
                value={currItem?.thumbnail}
                onChange={(value) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      thumbnail: value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" p={1}>
                Title
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Vietnamese"
                size="small"
                type="text"
                fullWidth
                inputProps={{
                  min: 1,
                }}
                value={currItem?.title_vi}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      title_vi: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="English"
                size="small"
                fullWidth
                type="text"
                inputProps={{
                  min: 1,
                }}
                value={currItem?.title_en}
                onChange={(e) => {
                  if (currItem) {
                    setCurrItem({
                      ...currItem,
                      title_en: e.currentTarget.value,
                    });
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Document File:</Typography>
            </Grid>

            <Grid sx={{ pt: 0 }} item xs={6}>
              <p>Vietnamese:</p>
              {currItem?.file_url_vi ? (
                <div style={{ display: 'flex' }}>
                  <a
                    style={{
                      flex: 1,
                    }}
                    href={process.env.REACT_APP_API_URI + currItem.file_url_vi}
                    target="_blank"
                    rel="noreferrer"
                    download={getFileName(currItem.file_url_vi)}
                  >
                    {getFileName(currItem.file_url_vi)}
                  </a>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => {
                      if (currItem)
                        setCurrItem({ ...currItem, file_url_vi: null });
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <TextField
                  size="small"
                  type="file"
                  fullWidth
                  onChange={(e) => {
                    const inputEl = e.currentTarget as HTMLInputElement;
                    const file = inputEl.files?.[0];
                    if (file) {
                      handleUploadFile('file_url_vi', file).then(() => {
                        inputEl.value = '';
                      });
                    }
                  }}
                />
              )}
            </Grid>
            <Grid sx={{ pt: 0 }} item xs={6}>
              <p>English:</p>
              {currItem?.file_url_en ? (
                <div style={{ display: 'flex' }}>
                  <a
                    style={{
                      flex: 1,
                    }}
                    href={process.env.REACT_APP_API_URI + currItem.file_url_en}
                    target="_blank"
                    rel="noreferrer"
                    download={getFileName(currItem.file_url_en)}
                  >
                    {getFileName(currItem.file_url_en)}
                  </a>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => {
                      if (currItem)
                        setCurrItem({ ...currItem, file_url_en: null });
                    }}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <TextField
                  size="small"
                  type="file"
                  fullWidth
                  onChange={(e) => {
                    const inputEl = e.currentTarget as HTMLInputElement;
                    const file = inputEl.files?.[0];
                    if (file) {
                      handleUploadFile('file_url_en', file).then(() => {
                        inputEl.value = '';
                      });
                    }
                  }}
                />
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrItem(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            variant="contained"
            onClick={() => {
              if (!currItem) {
                return;
              }
              Promise.resolve()
                .then(() => {
                  setDeleting(true);
                })
                .then(() => {
                  if (currItem?.id) {
                    return documentApi
                      .update(currItem.id, currItem)
                      .then(() => {
                        toast.success('Update successful');
                      })
                      .catch((e) => {
                        toast.error(e.message ?? e.toString());
                        throw e;
                      });
                  }
                  return documentApi
                    .create(currItem)
                    .then(() => {
                      toast.success('Update successful');
                    })
                    .catch((e) => {
                      toast.error(e.message ?? e.toString());
                      throw e;
                    });
                })
                .then(() => {
                  loadData();
                })
                .finally(() => {
                  setCurrItem(undefined);
                  setDeleting(false);
                });
            }}
            color="primary"
          >
            {deleting ? (
              <CircularProgress size={16} />
            ) : currItem?.id ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
